/**
 * Deterministic "last ordered" social proof generator.
 * Based on product ID + UTC time bucket, so ALL visitors on ALL devices see
 * the SAME message at the same moment. Uses no real customer data — DSGVO-safe.
 *
 * Key design:
 *  - A deterministic "order timestamp" is computed per product+bucket.
 *  - The "X minutes ago" label is derived live from (now - orderTimestamp),
 *    so refreshing 5 min later genuinely shows 5 min more elapsed time.
 *  - Cities are weighted: bigger/well-known Swiss cities appear far more often.
 *  - 100% client-side, zero database calls — no Cloud credits consumed.
 */

// Schweizer Städte mit Gewichtung (höhere Zahl = häufigeres Erscheinen).
// Grosse, bekannte Städte dominieren — kleinere Orte sind nur selten dabei.
const WEIGHTED_CITIES: Array<[string, number]> = [
  // Top-Tier (sehr häufig) — die grössten/bekanntesten CH-Städte
  ['Zürich', 30], ['Genf', 22], ['Basel', 20], ['Bern', 18], ['Lausanne', 16],
  ['Winterthur', 12], ['Luzern', 12], ['St. Gallen', 10], ['Lugano', 9], ['Biel', 8],
  // Mittlere Städte (regelmässig)
  ['Thun', 6], ['Schaffhausen', 6], ['Freiburg', 6], ['Chur', 6], ['Neuchâtel', 6],
  ['Sion', 5], ['Zug', 5], ['Aarau', 5], ['Baden', 5], ['Solothurn', 5],
  ['Bellinzona', 4], ['Locarno', 4], ['Montreux', 4], ['Vevey', 4], ['Nyon', 4],
  ['Uster', 4], ['Dübendorf', 4], ['Rapperswil-Jona', 4], ['Kreuzlingen', 4], ['Wil', 4],
  ['Olten', 4], ['Wetzikon', 3], ['Frauenfeld', 3], ['Liestal', 3], ['Burgdorf', 3],
  // Kleinere Städte (gelegentlich)
  ['Köniz', 2], ['La Chaux-de-Fonds', 2], ['Vernier', 2], ['Lancy', 2], ['Emmen', 2],
  ['Kriens', 2], ['Dietikon', 2], ['Allschwil', 2], ['Renens', 2], ['Riehen', 2],
  ['Wettingen', 2], ['Carouge', 2], ['Onex', 2], ['Kloten', 2], ['Meyrin', 2],
  ['Horgen', 2], ['Wädenswil', 2], ['Adliswil', 2], ['Bülach', 2], ['Schlieren', 2],
  ['Pratteln', 2], ['Wallisellen', 2], ['Muttenz', 2], ['Volketswil', 2], ['Gossau', 2],
  ['Yverdon-les-Bains', 2], ['Bulle', 2], ['Sierre', 2], ['Martigny', 2], ['Brig-Glis', 2],
  ['Davos', 2], ['Interlaken', 2], ['Langenthal', 2], ['Herisau', 2], ['Sursee', 2],
  // Selten (kleine Orte) — Long Tail
  ['Baar', 1], ['Pully', 1], ['Thônex', 1], ['Regensdorf', 1], ['Münchenstein', 1],
  ['Uzwil', 1], ['Reinach', 1], ['Thalwil', 1], ['Steffisburg', 1], ['Zollikon', 1],
  ['Lyss', 1], ['Morges', 1], ['Wohlen', 1], ['Stäfa', 1], ['Küsnacht', 1],
  ['Meilen', 1], ['Spiez', 1], ['Naters', 1], ['Romanshorn', 1], ['Ostermundigen', 1],
];

// Vorberechnete kumulative Gewichte für O(log n) Auswahl
const CITY_WEIGHTS_TOTAL = WEIGHTED_CITIES.reduce((sum, [, w]) => sum + w, 0);
const CITY_CUMULATIVE: number[] = (() => {
  const out: number[] = [];
  let acc = 0;
  for (const [, w] of WEIGHTED_CITIES) {
    acc += w;
    out.push(acc);
  }
  return out;
})();

function pickWeightedCity(seed: number): string {
  const target = seed % CITY_WEIGHTS_TOTAL;
  // Linearer Scan reicht (≈80 Einträge), kein Hot-Path
  for (let i = 0; i < CITY_CUMULATIVE.length; i++) {
    if (target < CITY_CUMULATIVE[i]) return WEIGHTED_CITIES[i][0];
  }
  return WEIGHTED_CITIES[0][0];
}

function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export interface LastOrderInfo {
  city: string;
  /** Localized "ago" label, e.g. "vor 2 Std." */
  agoLabel: string;
  /** UTC timestamp (ms) when the simulated order happened — useful for live re-rendering. */
  orderTimestamp: number;
}

export type SocialProofTier = 'topseller' | 'popular' | 'medium' | 'low' | 'niche';

export interface SocialProofTierInfo {
  key: SocialProofTier;
  label: string;
  /** German short range, e.g. "2–90 Min" */
  range: string;
  /** Tailwind classes for badge */
  badgeClass: string;
}

/**
 * Returns the tier classification based on REAL orders in the last 30 days.
 * Live, dynamic categorization — no hardcoded baselines.
 */
export function getSocialProofTier(ordersLast30Days: number): SocialProofTierInfo {
  if (ordersLast30Days >= 100) {
    return { key: 'topseller', label: '🔥 TopSeller', range: '2–90 Min', badgeClass: 'bg-red-500/10 text-red-600 border-red-500/20' };
  }
  if (ordersLast30Days >= 40) {
    return { key: 'popular', label: '⭐ Beliebt', range: '5 Min – 3 Std', badgeClass: 'bg-orange-500/10 text-orange-600 border-orange-500/20' };
  }
  if (ordersLast30Days >= 15) {
    return { key: 'medium', label: '✓ Mittel', range: '30 Min – 6 Std', badgeClass: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' };
  }
  if (ordersLast30Days >= 5) {
    return { key: 'low', label: '· Selten', range: '1–12 Std', badgeClass: 'bg-sky-500/10 text-sky-600 border-sky-500/20' };
  }
  return { key: 'niche', label: '○ Nische', range: '2–48 Std', badgeClass: 'bg-muted text-muted-foreground border-border' };
}

/**
 * Returns a deterministic last-order display for a product.
 *
 * IMPORTANT: every visitor on every device sees the SAME order timestamp at
 * the same moment in time, because the bucket is derived from UTC and the
 * product id only — no random, no per-session state.
 *
 * The age label is computed LIVE from (now - orderTimestamp), so refreshing
 * 5 min later truly shows 5 min more elapsed time, until the next bucket
 * rotation picks a fresh order.
 *
 * Bucket length scales with 30-day order volume so high-traffic products
 * rotate more frequently than niche ones (which would feel stale otherwise).
 */
export function getLastOrder(
  productId: string,
  productSlug: string,
  locale: string = 'de',
  ordersLast30Days: number = 15,
  nowMs: number = Date.now()
): LastOrderInfo {
  // Bucket length (minutes) — how often the simulated order rotates.
  // Shorter buckets for hot products, longer for niche ones.
  let bucketMinutes: number;
  // Time range (in minutes) the simulated order can live in the past, before
  // the next bucket starts. Picked so the visible age stays plausible.
  let minMinutes: number;
  let maxMinutes: number;

  if (ordersLast30Days >= 100)      { bucketMinutes = 60;   minMinutes = 2;   maxMinutes = 90; }
  else if (ordersLast30Days >= 40)  { bucketMinutes = 180;  minMinutes = 5;   maxMinutes = 180; }
  else if (ordersLast30Days >= 15)  { bucketMinutes = 360;  minMinutes = 30;  maxMinutes = 360; }
  else if (ordersLast30Days >= 5)   { bucketMinutes = 720;  minMinutes = 60;  maxMinutes = 720; }
  else                               { bucketMinutes = 1440; minMinutes = 120; maxMinutes = 2880; }

  const bucketMs = bucketMinutes * 60 * 1000;
  const bucketIndex = Math.floor(nowMs / bucketMs);
  const bucketStartMs = bucketIndex * bucketMs;

  const seed = hashString(`${productId}|${productSlug}|${bucketIndex}`);

  const city = pickWeightedCity(seed);

  // Pick a stable "minutes-ago-at-bucket-start" value. As real time passes
  // within the bucket, the live age (now - orderTimestamp) grows naturally.
  const range = maxMinutes - minMinutes;
  const minutesAgoAtBucketStart = minMinutes + (seed % range);
  const orderTimestamp = bucketStartMs - minutesAgoAtBucketStart * 60 * 1000;

  const liveMinutesAgo = Math.max(0, Math.round((nowMs - orderTimestamp) / 60000));
  const agoLabel = formatAgo(liveMinutesAgo, locale);

  return { city, agoLabel, orderTimestamp };
}

function formatAgo(minutes: number, locale: string): string {
  const labels: Record<string, { minAgo: (n: number) => string; hourAgo: (n: number) => string; dayAgo: (n: number) => string; justNow: string }> = {
    de: {
      justNow: 'gerade eben',
      minAgo: (n) => `vor ${n} Min.`,
      hourAgo: (n) => `vor ${n} ${n === 1 ? 'Std.' : 'Std.'}`,
      dayAgo: (n) => `vor ${n} ${n === 1 ? 'Tag' : 'Tagen'}`,
    },
    en: {
      justNow: 'just now',
      minAgo: (n) => `${n} min ago`,
      hourAgo: (n) => `${n} ${n === 1 ? 'hour' : 'hours'} ago`,
      dayAgo: (n) => `${n} ${n === 1 ? 'day' : 'days'} ago`,
    },
    fr: {
      justNow: "à l'instant",
      minAgo: (n) => `il y a ${n} min`,
      hourAgo: (n) => `il y a ${n} h`,
      dayAgo: (n) => `il y a ${n} ${n === 1 ? 'jour' : 'jours'}`,
    },
    it: {
      justNow: 'poco fa',
      minAgo: (n) => `${n} min fa`,
      hourAgo: (n) => `${n} ${n === 1 ? 'ora' : 'ore'} fa`,
      dayAgo: (n) => `${n} ${n === 1 ? 'giorno' : 'giorni'} fa`,
    },
  };

  const l = labels[locale] || labels.de;
  if (minutes < 2) return l.justNow;
  if (minutes < 60) return l.minAgo(minutes);
  if (minutes < 24 * 60) {
    const hours = Math.round(minutes / 60);
    return l.hourAgo(hours);
  }
  const days = Math.round(minutes / (60 * 24));
  return l.dayAgo(days);
}